import { useEffect, useId, useRef } from "react";
import { CloseButton } from "@/components/CloseButton";

/**
 * A dialog you can get out of.
 *
 * HQ-PROD-MODAL-001 and HQ-BUG-A11Y-001. Three pages had each written their own
 * ModalShell, months apart, and they disagreed about the basics: one had a
 * close button, two had none at all. The Link-task and formula-history dialogs
 * therefore offered no Cancel, no ×, and no Escape — the only ways out were
 * clicking the backdrop, which nothing told you about, or reloading the page.
 *
 * A dialog that can only be left by guessing is not a read-only dialog; it is a
 * trap. And for anyone not using a mouse it was exactly that, since the
 * backdrop is not reachable from the keyboard.
 *
 * So, once, properly:
 *   - role="dialog" aria-modal, named by its own heading via aria-labelledby,
 *     so a screen reader announces what opened rather than "dialog"
 *   - a close button with a real name, not a bare glyph
 *   - Escape closes it
 *   - focus moves in when it opens and returns to whatever opened it on close,
 *     so the keyboard does not end up back at the top of the document
 *   - Tab stays inside while it is open
 *
 * `dismissable={false}` is for a dialog mid-save, where leaving would abandon
 * work in flight — it disables Escape, the backdrop and the button together,
 * rather than leaving one of the three still working.
 */
export function ModalShell({
  title, onClose, children, wide, dismissable = true, closeLabel,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
  dismissable?: boolean;
  closeLabel?: string;
}) {
  const headingId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);

  // HQ-BUG-MODAL-FOCUS-001. "i cant even type in the field."
  //
  // This effect used to list [dismissable, onClose] as its dependencies, which
  // reads as careful and was the whole bug. Every caller passes an inline arrow
  // for onClose, so its identity changes on every render — and in a dialog with
  // controlled inputs, every keystroke IS a render. So between one character and
  // the next, React tore this effect down and set it up again: the cleanup threw
  // focus back to whatever opened the dialog, and the setup parked it on the
  // panel. A field therefore accepted exactly one character per click, and the
  // bug-report dialog — the thing people use to tell us something is broken —
  // was the most visible casualty.
  //
  // The handler still needs the CURRENT onClose and dismissable, or Escape would
  // close a dialog that had since become non-dismissable. So the live values go
  // in a ref that every render updates, and the effect itself runs once: focus
  // moves in on open and back out on close, which is the only time either should
  // happen.
  const latest = useRef({ onClose, dismissable });
  latest.current = { onClose, dismissable };

  useEffect(() => {
    opener.current = document.activeElement;
    // Focus the panel itself rather than the first control: landing on "Delete"
    // because it happens to be first is worse than landing nowhere.
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && latest.current.dismissable) { e.stopPropagation(); latest.current.onClose(); return; }
      if (e.key !== "Tab") return;
      const focusable = panel.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (!focusable?.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panel.current)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      // Back where they came from. Without this the keyboard resumes at the
      // top of the document, which is a long way from the button they pressed.
      (opener.current as HTMLElement | null)?.focus?.();
    };
    // Mount and unmount only. See the note above: anything that re-runs this
    // effect moves the user's focus out from under them mid-sentence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-ink/60 p-4"
      onClick={dismissable ? onClose : undefined}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        className={`card my-8 w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-auto outline-none`}
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <h2 id={headingId} className="text-lg font-semibold">{title}</h2>
          <CloseButton onClick={onClose} disabled={!dismissable} label={closeLabel ?? `Close ${title}`} />
        </div>
        {children}
      </div>
    </div>
  );
}
